<template>
  <b-form-group :class="['multiselect-form-group', groupClass, { hasSelectedTags: hasSelectedTags, isOverflowing: isOverflowing }]" :state="state">
    <label v-if="label" class="form__label input-label" :for="id">{{ label }}</label>
    <multiselect :ref="refKey" :value="value" :id="id" :options="options" :track-by="trackBy" :label="optionLabel"
      :placeholder="placeholder" :showLabels="false" :limit="maxVisibleTags" :multiple="multiple"
      class="signup-multiselect multiselect__single-line" @input="$emit('input', $event)">

      <template v-slot:limit>
        <span v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
          :title="value.slice(maxVisibleTags).map(el => el[optionLabel]).join(', ')">
          <template v-if="maxVisibleTags > 0">
            and {{ value.length - maxVisibleTags }} more
          </template>
          <template v-else>
          {{ value.length }}/{{ options.length }}
          </template>
        </span>
      </template>
      <template v-slot:tag="{ option, remove }">
        <span class="custom-tag-class" ref="tagRefs" style="
            display: inline-flex;
            align-items: center;
            background-color: #F3F5F7;
            border-radius: 3px;
            color: #3d5170;
            padding: 2px 4px;
            height: 20px;
            margin-right: 2px;
            white-space: nowrap;
            overflow: hidden;
            font-size: 14px;
            text-overflow: ellipsis;
            max-width: 100%;
          ">
          <span style="
              display: block;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            ">{{ optionLabel ? option[optionLabel] : option }}</span>
          <span style="margin-left: 2px; cursor: pointer" @click.prevent
            @mousedown.prevent.stop="remove(option, $event)" class="material-icons">close</span>
        </span>
      </template>
    </multiselect>
    <template #invalid-feedback>
      <slot name="invalid-feedback"></slot>
    </template>
  </b-form-group>
</template>

<script>
const initialMaxVisibleTags = 99
const moreIndicatorMaxWidth = 95

export default {
  name: 'SignUpMultiselect',
  props: {
    value: {
      type: [Object, null],
      default: null
    },
    state: {
      type: [Boolean, null],
      default: null
    },
    id: {
      type: String,
      required: true
    },
    label: String,
    options: {
      type: Array,
      required: true
    },
    trackBy: {
      type: String,
      required: true
    },
    optionLabel: {
      type: String,
      required: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    placeholder: String,
    groupClass: String
  },
  data () {
    return {
      containerWidth: 0,
      tagWidths: [],
      maxVisibleTags: initialMaxVisibleTags,
      hasSelectedTags: false,
      observer: null,
      isOverflowing: false
    }
  },
  computed: {
    refKey () {
      return this.id
    },
    getFieldValue: {
      get () {
        return this.value
      },
      set (newValue) {
        this.$emit('input', newValue)
        this.$emit('update', this.$vnode.key, this.field)
      }
    }
  },
  mounted () {
    this.updateContainerWidth()
    this.observeResize()
  },
  watch: {
    'getFieldValue': {
      handler (values) {
        this.isOverflowing = false
        this.maxVisibleTags = initialMaxVisibleTags // if tag is added we need make sure it's rendered to correctly calculate tags width
        this.$nextTick(() => {
          this.updateTagWidths(values)
        })
      }
    }
  },
  methods: {
    observeResize () {
      const element =
        this.$refs[this.refKey]?.$el?.querySelector('.multiselect__tags')
      if (!element) return

      this.observer = new ResizeObserver(() => {
        this.maxVisibleTags = initialMaxVisibleTags
        this.updateContainerWidth()
        this.$nextTick(() => {
          this.updateTagWidths(this.value)
        })
      })

      this.observer.observe(element)
    },
    updateContainerWidth () {
      const element =
        this.$refs[this.refKey]?.$el?.querySelector('.multiselect__tags')
      if (!element) return

      const computedStyle = window.getComputedStyle(element)
      const width = element.clientWidth
      const paddingLeft = parseFloat(computedStyle.paddingLeft)
      const paddingRight = parseFloat(computedStyle.paddingRight)

      this.containerWidth = width - paddingLeft - paddingRight
    },
    updateTagWidths (values) {
      const tags = this.$refs[this.refKey]?.$el?.querySelectorAll('.custom-tag-class')
      if (tags?.length === 0 || !this.containerWidth) {
        this.hasSelectedTags = false
        return
      }

      this.hasSelectedTags = true
      this.tagWidths = tags?.length ? Array.from(tags).map((tag) => tag.clientWidth) : 0
      this.calc(values?.length)
    },
    calc (valuesCount) {
      const totalTagsWidth = this.tagWidths?.length ? this.tagWidths.reduce((total, width) => total + width, 0) : 0
      const areAllTagsFit = totalTagsWidth <= this.containerWidth

      if (areAllTagsFit) {
        this.maxVisibleTags = valuesCount
        return
      }

      let availableSpace = this.containerWidth - moreIndicatorMaxWidth
      let tagIndex = 0
      let tagsToShow = 0

      while (this.tagWidths[tagIndex]) {
        availableSpace -= this.tagWidths[tagIndex]

        if (availableSpace < 0) {
          if (tagsToShow === 0) {
            tagsToShow = 1
          }
          this.isOverflowing = true
          break
        }
        this.isOverflowing = false
        tagIndex++
        tagsToShow++
      }

      this.maxVisibleTags = tagsToShow
    }
  }
}
</script>

<style scoped lang="scss">

.hasSelectedTags ::v-deep .multiselect__tags-wrap {
  display: inline-block;
  white-space: nowrap;
}

.isOverflowing ::v-deep .multiselect__tags-wrap {
  width: calc(100% - 95px) !important;
}

.hasSelectedTags ::v-deep .multiselect__single {
  display: none;
}

.multiselect-form-group.is-invalid .signup-multiselect:not(.multiselect--active) {
  ::v-deep .multiselect__tags {
    border-color: $input-border-color-invalid;
  }
}

.signup-multiselect ::v-deep .multiselect__tags-wrap {
  // display: inline-block !important;
  // align-items: center !important;
}

.signup-multiselect {
  ::v-deep .multiselect__select {
    height: $sign-in-input-height;
    width: $sign-in-multiselect-select-width;

    &::before {
      font-size: $sign-in-multiselect-select-font-size;
      line-height: $sign-in-input-height;
    }
  }

  ::v-deep .multiselect__strong {
    font-size: $sign-in-input-tag-font-size;
  }

  ::v-deep .multiselect__tags-wrap {
    height: 30px;
  }

  ::v-deep .multiselect__tags {
    width: 100%;
    padding: 10px 25px 10px $sign-in-input-padding-left;
    border-radius: $sign-in-input-border-radius;
    background-color: $sign-in-input-background-color;
    font-size: $sign-in-input-font-size;

    .multiselect__input,
    input::placeholder,
    .multiselect__single {
      font-size: $sign-in-input-font-size;
    }
  }

  ::v-deep .multiselect__tag {
    font-size: $sign-in-input-tag-font-size;
    margin-bottom: unset;
  }

  &.multiselect--active:not(.multiselect--above) {
    ::v-deep .multiselect__tags {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    ::v-deep .multiselect__content-wrapper {
      border-bottom-left-radius: $sign-in-input-border-radius;
      border-bottom-right-radius: $sign-in-input-border-radius;
    }
  }

  &.multiselect--active.multiselect--above {
    ::v-deep .multiselect__tags {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    ::v-deep .multiselect__content-wrapper {
      border-top-left-radius: $sign-in-input-border-radius;
      border-top-right-radius: $sign-in-input-border-radius;
    }
  }

  ::v-deep .multiselect__content-wrapper {
    background-color: $sign-in-input-background-color;
  }

  ::v-deep .multiselect__option {
    padding: 10px $sign-in-input-padding-left;
    font-size: $sign-in-input-font-size;
  }

}

.multiselect--active ::v-deep .multiselect__tags-wrap {
    display: none;
  }
</style>
